import { colors } from 'hui';
import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import HeaderContext from '../../HeaderContext';
import { GA_CATEGORY } from '../constants';

const MenuList = styled.ul<{ isMobile: boolean }>`
  list-style: none;
  padding: ${props => (props.isMobile ? '0' : '0 10px')};
  margin: 0;
`;

const MenuSection = styled.div`
  border-bottom: 1px solid ${colors.tint3};
`;

export const MenuLink = styled.a<{ primary?: boolean }>`
  font-size: 15px;
  text-decoration: none;
  white-space: nowrap;
  color: ${props => props.theme.colors.tint2};
  padding: 10px 10px 10px 0;
  margin: 3px 0;
  display: inline-block;
  &:hover,
  &:visited {
    color: #555;
  }

  ${props =>
    props.primary &&
    css`
      color: ${props.theme.colors.primary};
      &:hover,
      &:visited {
        color: ${props.theme.colors.primary};
      }
    `};
`;

const MenuListItem: React.FC<{
  href: string;
  title: string;
  gaAction: string;
  siteSource: string;
  hasNoFollow?: boolean;
}> = ({ href, title, gaAction, siteSource, hasNoFollow }) => (
  <li>
    <MenuLink
      href={href}
      title={title}
      data-ga-category={GA_CATEGORY}
      data-ga-action={`${GA_CATEGORY} - Clicked ${gaAction}`}
      data-ga-label={`${GA_CATEGORY} - ${siteSource}`}
      rel={hasNoFollow ? 'nofollow' : undefined}
    >
      {title}
    </MenuLink>
  </li>
);

const mainSection = (siteSource: string) => [
  {
    href: '/',
    title: 'Home',
    gaAction: 'Home - Mobile',
    siteSource,
  },
];

const businessSection = (siteSource: string) => [
  {
    href: '/get_quotes_simple',
    title: 'Get Quotes',
    gaAction: 'Get Quotes - Mobile',
    siteSource,
  },
  {
    href: 'https://hipagesconsumer.app.link/Ff70zWWpzR',
    title: 'Download the App',
    gaAction: 'Download the App - Mobile',
    siteSource,
    hasNoFollow: true,
  },
  {
    href: '/tradie/',
    title: 'List Your Business',
    gaAction: 'List Your Business - Mobile',
    siteSource,
  },
];

const publicSection = (siteSource: string) => [
  {
    href: '/find',
    title: 'Directory',
    gaAction: 'Directory - Mobile',
    siteSource,
  },
  {
    href: '/articles',
    title: 'Articles',
    gaAction: 'Articles - Mobile',
    siteSource,
  },
];

const userLoginSectionA = (siteSource: string) => [
  {
    href: '/account/?m=job',
    title: 'My Jobs',
    gaAction: 'My Toolbox - My Jobs',
    siteSource,
  },
];

const voucherSection = (siteSource: string) => [
  {
    href: '/account/voucher-offer',
    title: 'My vouchers',
    gaAction: 'My Toolbox - My vouchers',
    siteSource,
  },
];

const feedSection = (siteSource: string) => [
  {
    href: '/account/feed',
    title: 'Community',
    gaAction: 'My Toolbox - Community',
    siteSource,
  },
];

const userLoginSectionB = (siteSource: string) => [
  {
    href: '/account/feed',
    title: 'My Community',
    gaAction: 'My Toolbox - My Community',
    siteSource,
  },
  {
    href: '/message_centre',
    title: 'Messages',
    gaAction: 'My Toolbox - Message Centre',
    siteSource,
  },
  {
    href: '/account/edit',
    title: 'Edit Profile',
    gaAction: 'User Menu - My Edit Profile',
    siteSource,
  },
  {
    href: '/account/?m=dashboard&a=change_password',
    title: 'Change Password',
    gaAction: 'User Menu - Change Password',
    siteSource,
  },
  {
    href: '/account/subscribe',
    title: 'Unsubscribe',
    gaAction: 'Clicked User Menu - Unsubscribe',
    siteSource,
  },
  {
    href: '/account/?m=public&a=logout',
    title: 'Logout',
    gaAction: 'User Menu - Logout',
    siteSource,
  },
];

type MenuItem = {
  href: string;
  title: string;
  gaAction: string;
  siteSource: string;
  hasNoFollow?: boolean;
};

const userJoinSection = (siteSource: string) => [
  {
    href: '/register',
    title: 'Register',
    gaAction: 'My Sign Up - Mobile',
    siteSource,
    hasNoFollow: true,
  },
  {
    href: '/login',
    title: 'Sign in',
    gaAction: 'Login - Mobile',
    siteSource,
    hasNoFollow: true,
  },
];

const MenuItems: React.FC<{ items: MenuItem[] }> = ({ items }) => (
  <>
    {items.map(item => (
      <MenuListItem key={item.title} {...item} />
    ))}
  </>
);

export const MenuContents: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const { siteSource, loginData } = useContext(HeaderContext);
  const hasLoggedIn = loginData && loginData.logged_in ? true : false;
  const role = loginData && loginData.user_role;
  return (
    <MenuList isMobile={isMobile}>
      {isMobile && (
        <>
          <MenuSection>
            <MenuItems items={mainSection(siteSource)}></MenuItems>
          </MenuSection>
          <MenuSection>
            <MenuItems items={businessSection(siteSource)}></MenuItems>
          </MenuSection>
          <MenuSection>
            {<MenuItems items={publicSection(siteSource)}></MenuItems>}
            {!hasLoggedIn && <MenuItems items={feedSection(siteSource)} />}
          </MenuSection>
        </>
      )}
      {hasLoggedIn ? (
        <MenuSection>
          {<MenuItems items={userLoginSectionA(siteSource)}></MenuItems>}
          {role === 'consumer' && <MenuItems items={voucherSection(siteSource)}></MenuItems>}
          {<MenuItems items={userLoginSectionB(siteSource)}></MenuItems>}
        </MenuSection>
      ) : (
        <MenuSection>{<MenuItems items={userJoinSection(siteSource)}></MenuItems>}</MenuSection>
      )}
    </MenuList>
  );
};

export default MenuContents;
